import React from 'react';
import { observer } from 'mobx-react';

import {
  DeleteIcon,
  CurveIcon,
  MeasureProjectedIcon,
  MeasureTwoPointsIcon,
  MeasureRadiusIcon,
  MeasurePlaneOffsetIcon,
  MeasureAngleIcon,
  LineIcon,
} from 'icons';
import { Button, Paragraph } from 'components';
import { useCADManager } from 'hooks';
import { useContextManager } from 'apps/context/hooks';

import { themes } from 'styles/themes/wikifactoryNew';
import { renderCustomIcon } from 'utils/jsxUtils';

import {
  MeasureItemStyled,
  MeasureItemIcon,
  MeasureItemValue,
  MeasureItemProjection,
  MeasureItemDeleteWrapper,
} from './measurements.styled';

import { getMeasurementBody } from './utils';

const MeasureItem = observer(function _GeometryItem(props) {
  const {
    viewerControl,
    closeMeasurementFloatWindow,
    isOpenMeasurementFloatWindow,
  } = useCADManager();
  const contextManager = useContextManager();

  const { cadFile, viewer } = viewerControl;
  if (!viewer) return null;
  if (!cadFile) return null;

  const { pmi } = cadFile;
  const { module, notePad, selectionManager, settings } = viewer;
  if (!selectionManager) return null;

  const currCamera = settings.CurrentCamera();

  const msrEnum = module.MeasurementType;
  const { measure } = props;
  if (!measure) return null;

  const {
    isTemp,
    isNull,
    isFromTo,
    visible,
    type,
    value,
    annotationId,
    selfIx,
    forCadId,
  } = measure;

  const forCadItem = viewerControl.findCadItemForMeasurement(measure);

  if (isNull || !visible || Number.isNaN(value)) return null;

  const { valueStr, projValueStr } = getMeasurementBody(msrEnum, measure);

  const selected =
    !isTemp &&
    selectionManager.IsMkpSelected(type?.value, annotationId, selfIx, -1) >= 0;
  const highlighted =
    !isTemp &&
    selectionManager.IsMkpHighlighted(type?.value, annotationId, selfIx, -1);

  const icon = () => {
    if (isFromTo)
      // point -> point
      return <MeasureTwoPointsIcon />;

    switch (type) {
      case msrEnum.MeasurementDistance: // point -> point
        return <MeasureTwoPointsIcon />;
      case msrEnum.MeasurementDistanceOffset: // plane -> plane
        return <MeasurePlaneOffsetIcon />;
      case msrEnum.MeasurementLength: // line length
        return <LineIcon />;
      case msrEnum.MeasurementLengthCurvilinear: // curve length
        return <CurveIcon />;
      case msrEnum.MeasurementRadius: // radius
        return <MeasureRadiusIcon />;
      case msrEnum.MeasurementAngle: // angle
        return <MeasureAngleIcon />;
      default:
        break;
    }
    return null;
  };

  const handleOnClick = () => {
    if (!forCadItem) {
      return;
    }
    if (
      (forCadId === 0 || forCadId) &&
      forCadId !== currCamera.GetItem().uuid
    ) {
      viewerControl.InspectById(forCadId);
    } else {
      viewerControl.CameraToMeasurement(annotationId);
    }
  };
  const handleMouseEnter = () => {
    if (forCadItem) {
      viewerControl.SelectAndHighlightMeasurement(annotationId);
    }
  };
  const handleMouseLeave = () => {
    if (forCadItem) {
      viewerControl.UnHighlightEverything();
    }
  };

  return (
    <MeasureItemStyled
      isTemp={isTemp}
      selected={selected}
      highlighted={highlighted}
      onClick={handleOnClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ justifyContent: 'space-between' }}
    >
      <div className='flex items-center'>
        <div className='border rounded p-1 w-6 h-6 flex-center mr-1 border-wfGrey100'>
          <MeasureItemIcon>{icon()}</MeasureItemIcon>
        </div>
        <MeasureItemValue isStrikeThrough={!forCadItem && !measure.isTemp}>
          {valueStr}
        </MeasureItemValue>
        {projValueStr && (
          <MeasureItemProjection isProj>
            <MeasureProjectedIcon />
            <MeasureItemValue isProj isStrikeThrough={!forCadItem}>
              {projValueStr}
            </MeasureItemValue>
          </MeasureItemProjection>
        )}
        {!forCadItem && !measure.isTemp && (
          <Paragraph size='xsmall' variant='light'>
            &nbsp;(outdated)
          </Paragraph>
        )}
      </div>

      {!isTemp && (
        <MeasureItemDeleteWrapper
          onClick={e => {
            e.stopPropagation();

            if (selected) {
              // delete selection
              selectionManager.AddSelectionItem(measure._wasm);
            }

            if (!isTemp) {
              contextManager.DeleteAnnotation(annotationId);
            } else {
              pmi.AbortStartedMeasurement();
            }

            pmi.reportWasmChange();
            notePad.reportWasmChange();

            // fix delete open float window when float window is close.
            if (!isOpenMeasurementFloatWindow) {
              closeMeasurementFloatWindow();
            }
          }}
        >
          <Button
            renderIcon={hover =>
              renderCustomIcon(DeleteIcon, {
                isHover: hover,
                hoverFillColor: themes.color.wfRed,
              })
            }
            style={{ marginRight: -2 }}
            variant='tertiary'
            size='xsmall'
            appearance='plain'
          />
        </MeasureItemDeleteWrapper>
      )}
    </MeasureItemStyled>
  );
});

export default MeasureItem;
